import App from '@modules/husleie-framework/src/static-classes/App.class';
import LogOperations from '@modules/husleie-framework/src/static-classes/LogOperations.class';
import StringOperations from '@modules/husleie-framework/src/static-classes/StringOperations.class';
import Raven from 'raven-js';
import Vue from 'vue';
import VueI18n from 'vue-i18n';
import { Locale } from './enums/Locale';

Vue.use(VueI18n);

let vueI18n = null;

/**
 * i18n default translation formatter
 */
const defaultFormatter = new VueI18n().formatter;

function removeNameSpaces(path) {
  let returnList = path;
  if (returnList && typeof returnList === 'string') {
    returnList = returnList.split('.').slice(-1).pop();
  }
  return returnList;
}

function handlePlaceholders(path) {
  let returnList = path;
  if (returnList) {
    returnList = returnList.map(error => {
      if (typeof error === 'string') {
        return error.replaceAll('{dot}', '.');
      }
      return error;
    });
  }

  return returnList;
}

/**
 * i18n translation formatter
 *
 * If the translation files are missing, or the key/translation is missing, we revert to having the key as translation
 * If the key is nested, we retain the last entry of the nested hierarchy as translation
 * e.g. key 'signup.create user' -> 'create user' - we drop the 'signup' part
 *
 * Note, as '.' is the path separator, we cannot use it in the key string.
 * If we need a sentence to end with a '.', add '{dot}' at the end.
 *
 * Note: @:() links with whitespace's do not work, the link fails, so don't use links.
 * Use translations reuse in backoffice tool instead.
 */
class CustomFormatter {
  interpolate(message, values, path) {
    let returnList;
    // Term starts with i18n project name -> no term in CMS
    const noTermFound = message.startsWith(App.getTranslationsDefaultProject());
    const noTranslationFound = message === path;
    // Detect if help-text related term
    const isFieldHelpText = noTermFound && message.includes('.helpText.');

    if (vueI18n.translationsLoaded === undefined) {
      if (App.isDevelopment()) {
        LogOperations.error('Programmatic error: view rendered with undefined i18n library, path: ', path);
      }
    }

    if (noTranslationFound && isFieldHelpText) {
      // Help text are not considered mandatory
      returnList = [''];
    } else {
      if (vueI18n.translationsLoaded && noTranslationFound) {
        const warningMessage =
          `Translation not found for '${path}'` +
          ` (market '${App.getMarketIdentifier()}'` +
          `, environment '${App.environment()}'` +
          `, app '${App.getName()}'` +
          `, project '${App.getTranslationsDefaultProject()}'` +
          `, locale '${vueI18n.locale}')`;

        if (App.isProductionBuild()) {
          Raven.captureMessage(warningMessage);
          LogOperations.error('i18n', warningMessage);
        }

        if (App.isDevelopment()) {
          if (
            !App.getTranslationsMinimalLogLevel() ||
            App.getTranslationsMinimalLogLevel() === 'info' ||
            App.getTranslationsMinimalLogLevel() === 'warning'
          ) {
            LogOperations.warning('i18n', warningMessage);
          }
        }
      }

      if (values) {
        returnList = defaultFormatter.interpolate(message, values, path);
        if (returnList) {
          if (noTranslationFound && returnList[0]) {
            returnList[0] = removeNameSpaces(returnList[0]);
            if (returnList[0] === '') {
              returnList.shift(); // remove empty term at [0]
            }
          }
          if (returnList.length && message.endsWith('{dot}')) {
            // auto replace placeholder variable {dot} by '.'
            returnList[returnList.length - 1] = '.';
          }
        }
      } else {
        returnList = [noTranslationFound ? removeNameSpaces(message) : message];
      }

      // {dot} can still occur here when v-t is used without args, replace with '.'
      returnList = handlePlaceholders(returnList);
    }

    return returnList;
  }
}

function initialLocaleMessages() {
  return {
    en: {},
  };
}

const dateTimeFormats = {
  'en-GB': {
    short: {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    },
    long: {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      weekday: 'short',
      hour: 'numeric',
      minute: 'numeric',
    },
  },
  'no-NB': {
    short: {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    },
    long: {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      weekday: 'short',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
    },
  },
};

vueI18n = new VueI18n({
  formatFallbackMessages: true,
  formatter: new CustomFormatter(/* here the constructor options */),
  locale: Locale.EN,
  fallbackLocale: {
    /* 1 */ en_GB: [Locale.EN],
    /* 2 */ nb_NO: [Locale.NO, Locale.EN_GB, Locale.EN],
    /* 3 */ de_DE: [Locale.DE_DE, Locale.EN_GB, Locale.EN],
    /* 3 */ default: [Locale.EN],
  },
  messages: initialLocaleMessages(),
  silentTranslationWarn: true,
  silentFallbackWarn: true,
  preserveDirectiveContent: true,
  modifiers: {
    // Could not get these to work with $t() or v-t, they only seem to work in the json files
    capitalizeFirst: str => StringOperations.capitalizeFirst(str),
    capitalizeAll: str => StringOperations.capitalizeAll(str),
  },
  dateTimeFormats: dateTimeFormats,
  translationsLoaded: false,
});

export default vueI18n;
